import { motion } from 'framer-motion';
import { Target, Eye, Award, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const stats = [
    { label: 'Schools Served', value: '10+' },
    { label: 'Students Managed', value: '4.5K+' },
    { label: 'Team Members', value: '5+' },
    { label: 'Customer Satisfaction', value: '100%' },
  ];

  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description:
        'To empower educational institutions with intelligent technology that simplifies administration and enhances the learning experience for students, teachers, and parents.',
    },
    {
      icon: Eye,
      title: 'Our Vision',
      description:
        'To become India\'s most trusted school management platform, helping schools focus on education while we handle the operations seamlessly.',
    },
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl sm:text-6xl tracking-tighter font-black text-navy mb-6 font-heading">
              About VibhumEduSys
            </h1>
            <p className="text-base leading-relaxed text-slate-600 max-w-3xl mx-auto">
              We are passionate about transforming education through technology.
              Founded in 2016, VibhumEduSys has grown to become a leading school
              management solution trusted by institutions across India.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                data-testid={`stat-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-black text-emerald mb-2 font-heading">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {values.map((value, index) => (
              <motion.div
                key={index}
                data-testid={`value-${index}`}
                initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white p-10 rounded-2xl border border-slate-200 shadow-sm"
              >
                <div className="bg-emerald-light/10 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="h-8 w-8 text-emerald" />
                </div>
                <h2 className="text-2xl font-bold text-navy mb-4 font-heading">
                  {value.title}
                </h2>
                <p className="text-base text-slate-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
                Our Story
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  VibhumEduSys was born from a simple observation: schools spend too
                  much time on administrative tasks and too little on what truly
                  matters - educating students.
                </p>
                <p>
                  Founded by a team of educators and technologists in 2016, we set
                  out to build a comprehensive solution that would automate school
                  operations while being easy to use for everyone - from
                  administrators to parents.
                </p>
                <p>
                  Today, we proudly serve over 10 schools across India, managing
                  4,500+ students and processing millions in fee collections. Our
                  journey continues as we innovate and add new features based on
                  real feedback from our school partners.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1643180878811-2bc3d51a5627?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBzdHVkZW50cyUyMHNtaWxpbmd8ZW58MHx8fHwxNzc1NzkxMDUxfDA&ixlib=rb-4.1.0&q=85"
                alt="Students"
                className="rounded-2xl shadow-xl border border-slate-200"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-navy to-slate-800 text-white px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <Award className="h-16 w-16 text-emerald-light mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold mb-6 font-heading">
              Why Schools Trust Us
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div>
                <Users className="h-10 w-10 text-emerald-light mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Expert Team</h3>
                <p className="text-slate-300 text-sm">
                  Dedicated support from education and tech experts
                </p>
              </div>
              <div>
                <Award className="h-10 w-10 text-emerald-light mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Proven Track Record</h3>
                <p className="text-slate-300 text-sm">
                  8+ years of serving schools with 98% satisfaction
                </p>
              </div>
              <div>
                <Target className="h-10 w-10 text-emerald-light mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Continuous Innovation</h3>
                <p className="text-slate-300 text-sm">
                  Regular updates and new features based on your needs
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="cta-section" className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
            Ready To Partner With Us?
          </h2>
          <p className="text-base text-slate-600 mb-8">
            Join hundreds of schools transforming education with VibhumEduSys.
          </p>
          <Link to="/register">
            <Button
              data-testid="about-demo-button"
              size="lg"
              className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8 cta-button"
            >
              Schedule Demo
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
